/**
 * Resolve which graph to open for a P&ID / graph document, from its
 * `properties`. A document carries either:
 *   - `pnid_graph_ids`: a page→graph_id map (multi-page PDFs; may be a JSON
 *     string or an object), or
 *   - `graph_id`: a single graph id.
 *
 * Returns the primary `graphId` to open (first page, by page number) and, for
 * multi-page documents, a `pnidPages` string ("1:uuid,2:uuid") for the editor's
 * page navigation. Returns `{}` when no graph is resolvable yet (still
 * processing), so callers can show a "not ready" message.
 *
 * This mirrors the Data Catalog's open logic (routes/DataCatalog/index.tsx
 * handleRowClick) so every entry point — catalog row-click and Knowledge Graph
 * node preview — opens the exact same graph.
 */
export interface GraphOpenTarget {
  graphId?: string
  pnidPages?: string
  /** First sheet's page number (multi-page `pnid_graph_ids` only) — the page
   *  the editor opens on. Undefined for single graph_id docs. */
  firstPage?: number
}

/**
 * Serialise a page→graph map into the editor's `pnidPages` param
 * ("1:uuid,2:uuid"), sorted by page. Returns undefined below two pages, since
 * a lone sheet needs no navigation.
 *
 * Exported so callers holding the map in another shape — the PDF viewer reads
 * it from per-page classifications, not `pnid_graph_ids` — build the param
 * through this rather than re-deriving the format and drifting from the parser
 * in GraphEditor.
 */
export function formatPnidPages(
  entries: Array<{ page: number; graphId: string }>
): string | undefined {
  if (entries.length < 2) return undefined
  return [...entries]
    .sort((a, b) => a.page - b.page)
    .map((e) => `${e.page}:${e.graphId}`)
    .join(',')
}

/**
 * The `pnidPages` param for a multi-page P&ID, preferring the document's own
 * `pnid_graph_ids` and falling back to a per-page map assembled elsewhere.
 *
 * The precedence is the point. `pnid_graph_ids` is written for every page by
 * the multi-page fan-out and is what the catalog reads. Per-page classification
 * metadata carries a `graph_id` too, but it is written in a single place
 * (`routes/mixed_documents.py`) inside a vision-model success branch — so on an
 * automatically processed PDF some pages legitimately have none, and a map
 * built from it alone comes out short or empty, silently costing the reader
 * their sheet navigation.
 */
export function pnidPagesParamFor(
  properties: { graph_id?: string; pnid_graph_ids?: unknown } | null | undefined,
  fallbackEntries: Array<{ page: number; graphId: string }>
): string | undefined {
  return resolveGraphOpen(properties).pnidPages ?? formatPnidPages(fallbackEntries)
}

/** One sheet of a multi-page PDF: its 1-based page and that page's graph. */
export interface PnidPageEntry {
  page: number
  graphId: string
}

/**
 * Parse a document's `pnid_graph_ids` into page-ordered entries. Returns an
 * empty array when the document has no usable page map.
 *
 * Exported so the editor can derive its sheet list from the document itself
 * rather than from a URL param — the map is the same either way, but a
 * 133-sheet drawing serialises to a 5 KB query string.
 */
export function pnidPageEntriesFrom(
  properties: { graph_id?: string; pnid_graph_ids?: unknown } | null | undefined
): PnidPageEntry[] {
  const raw = properties?.pnid_graph_ids
  let pnidGraphIds: Record<string, string> | undefined
  try {
    pnidGraphIds =
      typeof raw === 'string'
        ? JSON.parse(raw)
        : raw && typeof raw === 'object' && !Array.isArray(raw)
          ? (raw as Record<string, string>)
          : undefined
  } catch {
    pnidGraphIds = undefined
  }

  // Reject anything that isn't a plain page→id object. `typeof x === 'object'`
  // is true for arrays (and null), and JSON.parse can also yield an array/
  // number/string — guarding here keeps an array like ["g-a","g-b"] from being
  // read as pages "0:g-a,1:g-b".
  if (
    !pnidGraphIds ||
    typeof pnidGraphIds !== 'object' ||
    Array.isArray(pnidGraphIds) ||
    Object.keys(pnidGraphIds).length === 0
  ) {
    return []
  }

  return Object.keys(pnidGraphIds)
    .sort((a, b) => Number(a) - Number(b))
    .map((p) => ({ page: Number(p), graphId: pnidGraphIds![p] }))
}

export function resolveGraphOpen(
  properties: { graph_id?: string; pnid_graph_ids?: unknown } | null | undefined
): GraphOpenTarget {
  if (!properties) return {}

  const entries = pnidPageEntriesFrom(properties)
  if (entries.length >= 1) {
    const graphId = entries[0].graphId
    const pnidPages = entries.length > 1 ? formatPnidPages(entries) : undefined
    const firstPage = Number.isNaN(entries[0].page) ? undefined : entries[0].page
    return { graphId, pnidPages, firstPage }
  }

  if (properties.graph_id) return { graphId: properties.graph_id }
  return {}
}

/**
 * Build the graph-editor URL for opening a P&ID/PFD catalog document, or
 * null when no graph is resolvable yet (still processing). Documents whose
 * graph came from the pnid_graph_ids page map (multi-page PDFs) carry
 * documentId so the editor can serve per-page images and sheet connectors —
 * and, from that same id, look the page map up for itself.
 *
 * The map is deliberately NOT serialised into the URL. It used to be, as
 * `pnidPages`, which is fine at three sheets and a 5 KB query string at 133 —
 * every sheet id repeated in a link whose owning document already stores them.
 * The editor still accepts the param when something else supplies it.
 */
export function graphEditorUrlForDocument(
  docId: string,
  properties: { graph_id?: string; pnid_graph_ids?: unknown } | null | undefined,
  /** Which graph to open in place of the resolved one; an SLD opens its parse. */
  chooseGraph: (graphId: string) => string = (graphId) => graphId,
): string | null {
  const target = resolveGraphOpen(properties)
  if (!target.graphId) return null
  const params = new URLSearchParams({ editor: 'd3' })
  // documentId is needed only when the graph came from the pnid_graph_ids
  // page map (multi-page PDFs). Detect that directly from pnid_graph_ids
  // rather than comparing graphIds — a doc whose graph_id coincidentally
  // equals its first-page entry would otherwise silently lose documentId,
  // and with it the editor's only route back to the page map.
  if (pnidPageEntriesFrom(properties).length >= 1) params.set('documentId', docId)
  return `/graph-editor/${chooseGraph(target.graphId)}?${params.toString()}`
}
